import React, { useState, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import {
  ShieldCheck, Share2, Download, TrendingUp, Wallet, Smartphone, Briefcase,
  CreditCard, Users, CheckCircle2, XCircle, AlertTriangle, Info,
} from 'lucide-react';

// ── SugarHathi brand palette (matches other KYC reports) ──────────────────
const ACCENT = '#f97316';
const ACCENT_DARK = '#ea580c';
const BG = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';
const GREEN = '#16a34a';
const GREEN_BG = '#f0fdf4';
const AMBER = '#d97706';
const AMBER_BG = '#fffbeb';
const RED = '#dc2626';
const RED_BG = '#fef2f2';
const SLATE_BG = '#f8fafc';

// ── Real Decentro OmniScore Advance payload (trimmed of nothing — this is
// the actual pull), used as the fallback SAMPLE when no live `data` prop is
// passed in (e.g. viewing this component standalone). ─────────────────────
const SAMPLE = {
  "data": {
    "risk": "Low Risk",
    "subScore": {
      "financialServices": {
        "score": 698,
        "message": "Decent penetration with a healthy mix of digital payments and formal banking. Moderate risk. Consider evaluating recent credit history before a decision.",
        "totalIncome": "1000000",
        "totalInquiries": "0",
        "numberOfAccounts": "25",
        "averageOpenBalance": "39518152.00",
        "delinquentAccounts": "2",
        "activeAccountsCount": "3"
      },
      "digitalIntelligence": {
        "score": 834,
        "circle": "Andhra Pradesh",
        "message": "Strong digital footprint with continuous and traceable activity across platforms. Historical consistency is visible. Feel free to hit mobile-to-address or persona APIs for deeper insights.",
        "operator": "Airtel",
        "isPostpaid": "UNKNOWN",
        "digitalAgeAge": "7+",
        "digitalAgeYear": "2018",
        "digitalAgeMonth": "12",
        "ageOnNetworkRange": "17 to 18 Years",
        "socialProfilesFound": ["flipkart","snapdeal","housing","facebook","microsoft","whatsapp","zoho","digilocker","nobroker","irctc"],
        "dateOfNetworkInitiation": "2009-04-01"
      },
      "employmentStability": {
        "uan": "101620836575",
        "score": 368,
        "message": "Spotty employment background with short stints or informal sector history. Possibly a gig worker or early in their career. Use employment APIs to confirm legitimacy.",
        "dateOfExit": "2024-08-31",
        "dateOfJoining": "2024-07-01",
        "establishmentName": "SRISTI BIO SCIENCES PRIVATE LIMITED"
      },
      "paymentIntelligence": {
        "message": "No fraudulent payment records found.",
        "isTransactionFraud": 0
      }
    },
    "omniscore": 647,
    "personalDetails": {
      "age": "44",
      "pan": "APPPM4486G",
      "state": "TS",
      "gender": "Male",
      "address": "B B BLOCK  VAISHNAVI OASIS APTS  BANDLAGUDA JAGIR RAN-TS",
      "fullName": "MENA KRISHNA ",
      "nameMatch": {
        "inputVsPan": { "textualMatch": "", "phoneticMatch": "" },
        "inputVsBureau": { "textualMatch": "80", "phoneticMatch": "78" }
      },
      "occupation": "",
      "postalCode": "500086",
      "addressType": "",
      "dateOfBirth": "1982-05-05",
      "emailAddress": "",
      "passportNumber": ""
    }
  },
  "status": "SUCCESS",
  "message": "OmniScore Fetched Successfully.",
  "responseKey": "success_omniscore_fetched",
  "responseCode": "S00000",
  "decentroTxnId": "1D410222D74A447CA77FFAFCED9819EA"
};

function formatMoney(v) {
  const n = Number(v);
  if (v === '' || v === null || v === undefined || Number.isNaN(n)) return '—';
  if (n === 0) return '\u20b90';
  return `\u20b9${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
function formatDateISO(s) {
  if (!s) return '—';
  const d = new Date(s + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function scoreTone(score) {
  if (score >= 700) return { fg: GREEN, bg: GREEN_BG, border: '#bbf7d0', label: 'Strong' };
  if (score >= 500) return { fg: AMBER, bg: AMBER_BG, border: '#fde68a', label: 'Moderate' };
  return { fg: RED, bg: RED_BG, border: '#fecaca', label: 'Weak' };
}
function riskTone(risk) {
  const r = (risk || '').toLowerCase();
  if (r.includes('low')) return { fg: GREEN, bg: GREEN_BG, border: '#bbf7d0' };
  if (r.includes('medium') || r.includes('moderate')) return { fg: AMBER, bg: AMBER_BG, border: '#fde68a' };
  return { fg: RED, bg: RED_BG, border: '#fecaca' };
}

function SectionCard({ title, icon: IconEl, children, footer }) {
  return (
    <div className="print-force-show" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
        {IconEl && <IconEl size={14} color={ACCENT_DARK} strokeWidth={2.5} />}
        <span style={{ fontSize: 12.5, fontWeight: 800, color: INK, letterSpacing: 0.2 }}>{title}</span>
      </div>
      <div>{children}</div>
      {footer && <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${LINE}` }}>{footer}</div>}
    </div>
  );
}

function StatBox({ label, value, tone = 'neutral' }) {
  const colors = {
    neutral: { fg: INK, bg: SLATE_BG, border: LINE },
    good: { fg: GREEN, bg: GREEN_BG, border: '#bbf7d0' },
    warn: { fg: AMBER, bg: AMBER_BG, border: '#fde68a' },
    bad: { fg: RED, bg: RED_BG, border: '#fecaca' },
  };
  const c = colors[tone];
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: '8px 10px' }}>
      <div style={{ fontSize: 9.5, color: SLATE, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: c.fg }}>{value}</div>
    </div>
  );
}

function DataRow({ sno, label, value, mono = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', borderBottom: `1px solid #f9fafb` }}>
      {sno !== undefined && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, width: 16, flexShrink: 0, paddingTop: 1 }}>{sno}</span>}
      <span style={{ fontSize: 11.5, color: SLATE, minWidth: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: INK, textAlign: 'right', flex: 1, fontFamily: mono ? MONO : undefined, wordBreak: 'break-word' }}>{value ?? '—'}</span>
    </div>
  );
}

// One sub-score block: big number + tone-colored bar + the vendor's own
// plain-language explanation (already human-readable from the API — not
// something we're paraphrasing).
function SubScoreBlock({ icon: IconEl, label, score, message, children }) {
  const tone = scoreTone(score);
  return (
    <div style={{ borderBottom: `1px solid ${LINE}`, padding: '12px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {IconEl && <IconEl size={13} color={SLATE} />}
          <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{label}</span>
        </div>
        <span style={{ fontSize: 15, fontWeight: 800, color: tone.fg }}>{score}<span style={{ fontSize: 10, color: SLATE, fontWeight: 500 }}> /1000</span></span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden', marginBottom: 8 }}>
        <div style={{ width: `${Math.min(100, (score / 1000) * 100)}%`, height: '100%', background: tone.fg, borderRadius: 3 }} />
      </div>
      <p style={{ fontSize: 11, color: SLATE, margin: 0, lineHeight: 1.5 }}>{message}</p>
      {children}
    </div>
  );
}

export default function OmniscoreAdminReport({ data = SAMPLE }) {
  const cardRef = useRef(null);
  const d = data.data;
  const fs = d.subScore.financialServices;
  const di = d.subScore.digitalIntelligence;
  const es = d.subScore.employmentStability;
  const pi = d.subScore.paymentIntelligence;
  const pd = d.personalDetails;
  const risk = riskTone(d.risk);

  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'omniscore-admin-report.pdf', BG); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `OmniScore — ${pd.fullName.trim()}\nScore: ${d.omniscore}/1000 (${d.risk})\nFinancial: ${fs.score} · Digital: ${di.score} · Employment: ${es.score}`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'omniscore-admin-report.pdf', 'OmniScore — Admin Report', shareText, BG); } finally { setPdfBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', background: BG, padding: '20px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } .print-force-show { display: block !important; } body { background: #fff !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 580, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button onClick={handleShare} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Share2 size={13} /> Share
          </button>
          <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px', color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
            <Download size={13} /> Download as PDF
          </button>
        </div>

        {/* Header */}
        <div style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`, borderRadius: 18, padding: '18px 20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5 }}>
              <ShieldCheck size={15} color="#fff" strokeWidth={2.5} />
              <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>OmniScore · Admin</span>
            </div>
            <h1 style={{ color: '#fff', fontSize: 17, fontWeight: 800, margin: 0 }}>{pd.fullName.trim()}</h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 11.5, margin: '2px 0 0', fontFamily: MONO, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>{pd.pan}</span>
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>·</span>
              <span>{pd.age} yrs · {pd.gender}</span>
            </p>
          </div>
        </div>

        {/* Overall score */}
        <SectionCard title="Composite OmniScore" icon={TrendingUp}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 44, fontWeight: 900, color: INK, lineHeight: 1 }}>{d.omniscore}</span>
            <span style={{ fontSize: 10.5, color: SLATE }}>/ 1000</span>
            <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800, color: risk.fg, background: risk.bg, border: `1px solid ${risk.border}`, borderRadius: 20, padding: '4px 12px' }}>{d.risk}</span>
          </div>
          <p style={{ fontSize: 10.5, color: SLATE, margin: 0, display: 'flex', gap: 5 }}>
            <Info size={11} style={{ flexShrink: 0, marginTop: 1 }} />
            Blended score across financial, digital, employment and payment-fraud signals — not a bureau credit score.
          </p>
        </SectionCard>

        {/* Sub-scores */}
        <SectionCard title="Sub-Scores" icon={Wallet}>
          <SubScoreBlock icon={Wallet} label="Financial Services" score={fs.score} message={fs.message}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
              <StatBox label="Accounts" value={fs.numberOfAccounts} />
              <StatBox label="Active" value={fs.activeAccountsCount} />
              <StatBox label="Delinquent" value={fs.delinquentAccounts} tone={Number(fs.delinquentAccounts) > 0 ? 'warn' : 'good'} />
              <StatBox label="Enquiries" value={fs.totalInquiries} />
              <StatBox label="Declared income" value={formatMoney(fs.totalIncome)} />
              <StatBox label="Avg. open balance" value={formatMoney(fs.averageOpenBalance)} />
            </div>
          </SubScoreBlock>

          <SubScoreBlock icon={Smartphone} label="Digital Intelligence" score={di.score} message={di.message}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
              <StatBox label="Digital age" value={`${di.digitalAgeAge} yrs`} />
              <StatBox label="On network" value={di.ageOnNetworkRange} />
              <StatBox label="Operator" value={di.operator} />
              <StatBox label="Circle" value={di.circle} />
              <StatBox label="Connection" value={di.isPostpaid === 'UNKNOWN' ? 'Unknown' : di.isPostpaid} />
              <StatBox label="Network since" value={formatDateISO(di.dateOfNetworkInitiation)} />
            </div>
            {di.socialProfilesFound?.length > 0 && (
              <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {di.socialProfilesFound.map(p => (
                  <span key={p} style={{ fontSize: 10, fontWeight: 700, color: ACCENT_DARK, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 999, padding: '3px 9px', textTransform: 'capitalize' }}>{p}</span>
                ))}
              </div>
            )}
          </SubScoreBlock>

          <SubScoreBlock icon={Briefcase} label="Employment Stability" score={es.score} message={es.message}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 8 }}>
              <StatBox label="UAN" value={es.uan} />
              <StatBox label="Employer" value={es.establishmentName} />
              <StatBox label="Joined" value={formatDateISO(es.dateOfJoining)} />
              <StatBox label="Exited" value={formatDateISO(es.dateOfExit)} />
            </div>
          </SubScoreBlock>

          <div style={{ padding: '12px 0 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <CreditCard size={13} color={SLATE} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>Payment Intelligence</span>
            </div>
            <div style={{
              background: pi.isTransactionFraud ? RED_BG : GREEN_BG, border: `1px solid ${pi.isTransactionFraud ? '#fecaca' : '#bbf7d0'}`,
              borderRadius: 10, padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center',
            }}>
              {pi.isTransactionFraud ? <XCircle size={14} color={RED} /> : <CheckCircle2 size={14} color={GREEN} />}
              <span style={{ fontSize: 11.5, fontWeight: 700, color: pi.isTransactionFraud ? RED : GREEN }}>{pi.message}</span>
            </div>
          </div>
        </SectionCard>

        {/* Personal Details */}
        <SectionCard title="Personal Details" icon={Users}>
          <DataRow sno={1} label="Full name" value={pd.fullName.trim()} />
          <DataRow sno={2} label="PAN" value={pd.pan} mono />
          <DataRow sno={3} label="Date of birth" value={formatDateISO(pd.dateOfBirth)} />
          <DataRow sno={4} label="Age" value={`${pd.age} yrs`} />
          <DataRow sno={5} label="Gender" value={pd.gender} />
          <DataRow sno={6} label="Occupation" value={pd.occupation || '—'} />
          <DataRow sno={7} label="Address" value={pd.address} />
          <DataRow sno={8} label="Postal code" value={pd.postalCode} mono />
          <DataRow sno={9} label="State" value={pd.state} />
          <DataRow sno={10} label="Email" value={pd.emailAddress || '—'} />
          <DataRow sno={11} label="Passport" value={pd.passportNumber || '—'} />
        </SectionCard>

        {/* Name Match */}
        <SectionCard
          title="Name Match"
          icon={AlertTriangle}
          footer={
            <p style={{ fontSize: 9.5, color: SLATE, margin: 0 }}>
              Blank values mean the bureau/PAN registry didn't return a name to compare against for that check.
            </p>
          }
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            <StatBox label="vs PAN — Textual" value={pd.nameMatch.inputVsPan.textualMatch ? `${pd.nameMatch.inputVsPan.textualMatch}%` : '—'} />
            <StatBox label="vs PAN — Phonetic" value={pd.nameMatch.inputVsPan.phoneticMatch ? `${pd.nameMatch.inputVsPan.phoneticMatch}%` : '—'} />
            <StatBox label="vs Bureau — Textual" value={pd.nameMatch.inputVsBureau.textualMatch ? `${pd.nameMatch.inputVsBureau.textualMatch}%` : '—'} tone={Number(pd.nameMatch.inputVsBureau.textualMatch) >= 70 ? 'good' : 'warn'} />
            <StatBox label="vs Bureau — Phonetic" value={pd.nameMatch.inputVsBureau.phoneticMatch ? `${pd.nameMatch.inputVsBureau.phoneticMatch}%` : '—'} tone={Number(pd.nameMatch.inputVsBureau.phoneticMatch) >= 70 ? 'good' : 'warn'} />
          </div>
        </SectionCard>

        <p style={{ fontSize: 9.5, color: '#9ca3af', textAlign: 'center', margin: '4px 0 0' }}>
          Decentro txn {data.decentroTxnId} · {data.responseCode}
        </p>
      </div>
    </div>
  );
}
